import * as Sentry from '@sentry/react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { GA4PageView } from "@/hooks/useGA4";
import { SiteTrafficBeacon } from "@/components/SiteTrafficBeacon";

// Standalone /account page — lazy so its ~466-line bundle only loads when
// signed-in users actually click "Account" from the header dropdown. It's
// mounted outside LocalSEOApp so lead-gen-only users aren't bounced away.
const AccountPage = lazy(() => import("./pages/AccountPage"));
// Chat widgets are the two biggest non-route dependencies that ran on
// every page (~50-80 kB gzip combined once you count Crisp's transitive
// deps + the FlyChatWidget's fetch calls). Lazy them and only mount
// after paint + idle so first-contentful-paint isn't blocked.
const CrispChatWrapper = lazy(() => import("@/components/CrispChatWrapper").then((m) => ({ default: m.CrispChatWrapper })));
const FlyChatWidget    = lazy(() => import("@/components/FlyChatWidget/FlyChatWidget").then((m) => ({ default: m.FlyChatWidget })));
import Index from "./pages/Index";
import FlyHomePage from "./pages/FlyHomePage";
const ManagedPage = lazy(() => import("./pages/ManagedPage"));
const ManagedThankYouPage = lazy(() => import("./pages/ManagedThankYouPage"));
const ManagedInvoice = lazy(() => import("./pages/public/ManagedInvoice"));
import FlyAgenciesPage from "./pages/AgenciesPage";
// /producthunt — V2 is the only landing shipped to production; V1 is
// archived to docs/archive/producthunt-v1/. Lazy so /producthunt's chunk
// only ships when someone actually visits it.
const ProductHuntPage = lazy(() => import("./pages/ProductHuntV2"));
// /offer — standalone offer-funnel landing page, first-class alongside
// /producthunt. Lazy so its chunk only ships when someone visits it.
// Formerly /ad; the old URL is preserved as a redirect to /offer at the
// route table below so any external ad links keep working.
const OfferPage = lazy(() => import("./pages/OfferPage"));
import FlyDIYPage from "./pages/FlyDIYPage";
import AgenciesFlyWebPage from "./pages/AgenciesFlyWebPage";
import AgenciesFlyWebThankYouPage from "./pages/AgenciesFlyWebThankYouPage";
import AgenciesFlyAgentPage from "./pages/AgenciesFlyAgentPage";
const GmbWhatsappAssistant = lazy(() => import("./pages/GmbWhatsappAssistant"));
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Cookie from "./pages/Cookie";
import NotFound from "./pages/NotFound";
// Solutions pages removed - site is now product-centric
// import MultiLocationBrands from "./pages/solutions/MultiLocationBrands";
// import MarketingAgencies from "./pages/solutions/MarketingAgencies";
const FlyVsBrightLocal = lazy(() => import("./pages/comparisons/FlyVsBrightLocal"));
const FlyVsMozLocal = lazy(() => import("./pages/comparisons/FlyVsMozLocal"));
const FlyVsYext = lazy(() => import("./pages/comparisons/FlyVsYext"));
// Removed: FlyVsWhitespark and FlyVsLocaliQ comparison pages
const Restaurants = lazy(() => import("./pages/industries/Restaurants"));
const Healthcare = lazy(() => import("./pages/industries/Healthcare"));
const Retail = lazy(() => import("./pages/industries/Retail"));
const Automotive = lazy(() => import("./pages/industries/Automotive"));
const ProfessionalServices = lazy(() => import("./pages/industries/ProfessionalServices"));
// Features pages removed - site is now product-centric
// import ProfileManagement from "./pages/features/ProfileManagement";
// import MultiLocationManagement from "./pages/features/MultiLocationManagement";
// import CustomerInsights from "./pages/features/CustomerInsights";
// import ReputationManagement from "./pages/features/ReputationManagement";
// import ContentManagement from "./pages/features/ContentManagement";
const LocalRankCheck = lazy(() => import("./pages/tools/LocalRankCheck"));
const GMBAudit = lazy(() => import("./pages/tools/GMBAudit"));
const ReviewAnalysis = lazy(() => import("./pages/tools/ReviewAnalysis"));
const CitationChecker = lazy(() => import("./pages/tools/CitationChecker"));
const FreeReviewQRGenerator = lazy(() => import("./pages/tools/FreeReviewQRGenerator"));
const ReviewQRPage = lazy(() => import("./pages/ReviewQRPage"));
const ReviewQRThankYouPage = lazy(() => import("./pages/ReviewQRThankYouPage"));
const FreeContentIdeaGenerator = lazy(() => import("./pages/tools/FreeContentIdeaGenerator"));
const SocialMediaPostGenerator = lazy(() => import("./pages/tools/SocialMediaPostGenerator"));
const ReviewGeneratorTool = lazy(() => import("./pages/tools/ReviewGeneratorTool"));
const FreeMicrositeTool = lazy(() => import("./pages/tools/FreeMicrositeTool"));
const FreeWebsiteBuilder = lazy(() => import("./pages/tools/FreeWebsiteBuilder"));
const PublicReviewPage = lazy(() => import("./pages/PublicReviewPageNew"));
const ContentReviewPage = lazy(() => import("./pages/public/ContentReviewPage"));
const ClientContentGeneratorPage = lazy(() => import("./pages/public/ClientContentGeneratorPage"));
const VerifyWhatsApp = lazy(() => import("./pages/public/VerifyWhatsApp"));
const ProfileUpdateReview = lazy(() => import("./pages/public/ProfileUpdateReview"));
const PublicReferenceImagesPage = lazy(() => import("./pages/public/PublicReferenceImagesPage"));
const PublicOnboardingPage = lazy(() => import("./pages/public/PublicOnboardingPage"));
const WhatsAppOnboarding = lazy(() => import("./pages/public/WhatsAppOnboarding"));
const WhatsAppOnboardingCallback = lazy(() => import("./pages/public/WhatsAppOnboardingCallback"));
const EmailUnsubscribe = lazy(() => import("./pages/public/EmailUnsubscribe"));
const PublicAnalyticsPage = lazy(() => import("./pages/PublicAnalyticsPage"));
const LandingPageGenerator = lazy(() => import("./pages/tools/LandingPageGenerator"));
const PublicLandingPage = lazy(() => import("./pages/PublicLandingPage"));
const ProfileProtection = lazy(() => import("./pages/tools/ProfileProtection"));
// Removed: DoctorCitationChecklist and GBPHealthQuiz - now redirect to gmb-audit
const FreeContentWizard = lazy(() => import("./pages/tools/FreeContentWizard"));
const EmbedToolPage = lazy(() => import("./pages/embed/EmbedToolPage"));
const GhlAppPage = lazy(() => import("./pages/ghl/GhlAppPage"));
const GbpDescriptionGenerator = lazy(() => import("./pages/tools/gbp-description-generator"));
const ReviewResponseGenerator = lazy(() => import("./pages/tools/review-response-generator"));
const SmmRoiCalculator = lazy(() => import("./pages/tools/smm-roi-calculator"));
const CaptionGenerator = lazy(() => import("./pages/tools/caption-generator"));
const BrandVoiceAnalyzer = lazy(() => import("./pages/tools/brand-voice-analyzer"));
const ContentCalendarGenerator = lazy(() => import("./pages/tools/content-calendar-generator"));
const LinkedInAudit = lazy(() => import("./pages/tools/linkedin-audit"));
const ReputationAudit = lazy(() => import("./pages/tools/reputation-audit"));
const InstagramAudit = lazy(() => import("./pages/tools/instagram-audit"));
const CitationAuditDeep = lazy(() => import("./pages/tools/citation-audit-deep"));
// Sprint 6
const GbpPostsScore = lazy(() => import("./pages/tools/gbp-posts-score"));
const RestaurantAudit = lazy(() => import("./pages/tools/restaurant-audit"));
const WebsiteLocalSeoAudit = lazy(() => import("./pages/tools/website-local-seo-audit"));
const SchemaValidator = lazy(() => import("./pages/tools/schema-validator"));
const SocialProfileAudit = lazy(() => import("./pages/tools/social-profile-audit"));
const EngagementRateCalculator = lazy(() => import("./pages/tools/engagement-rate-calculator"));
const FbAdsLibraryAudit = lazy(() => import("./pages/tools/fb-ads-library-audit"));
const CompetitorSocialAudit = lazy(() => import("./pages/tools/competitor-social-audit"));
const HashtagResearch = lazy(() => import("./pages/tools/hashtag-research"));
const BestTimeToPost = lazy(() => import("./pages/tools/best-time-to-post"));
const CompetitorAnalysisNearby = lazy(() => import("./pages/tools/competitor-analysis-nearby"));
const PostQualityScore = lazy(() => import("./pages/tools/post-quality-score"));
import LeadGenToolsLanding from "./pages/LeadGenToolsLanding";
const LocalSEOApp = lazy(() => import("./pages/LocalSEOApp"));
const ContentIdeaEditPage = lazy(() => import("./pages/fly-app/ContentIdeaEditPage"));
const SubscriptionCheckout = lazy(() => import("./pages/fly-app/SubscriptionCheckout"));
const CreditsPurchase = lazy(() => import("./pages/fly-app/CreditsPurchase"));
const AuthorizedNumbers = lazy(() => import("./pages/fly-app/AuthorizedNumbers"));
const CreditUsage = lazy(() => import("./pages/fly-app/CreditUsage"));
const TestGMBPro = lazy(() => import("./pages/TestGMBPro"));
const TestEmailNotifications = lazy(() => import("./pages/TestEmailNotifications"));
const GMBProDebug = lazy(() => import("./pages/GMBProDebug"));
const GMBProSimple = lazy(() => import("./pages/GMBProSimple"));
import ErrorBoundary from "./components/ErrorBoundary";
const BlogIndex = lazy(() => import("./pages/blogs/BlogIndex"));
const KeywordRankDetail = lazy(() => import("./pages/fly-app/KeywordRankDetail"));
const GoogleBusinessProfileSuspension = lazy(() => import("./pages/blogs/GoogleBusinessProfileSuspension"));
const GMBSuspensionRecoveryTime = lazy(() => import("./pages/blogs/GMBSuspensionRecoveryTime"));
const PostingFakeReviewsKeywordStuffing = lazy(() => import("./pages/blogs/PostingFakeReviewsKeywordStuffing"));
const UpdatingBusinessInfoGMBSuspension = lazy(() => import("./pages/blogs/UpdatingBusinessInfoGMBSuspension"));
const MultipleProfilesBackfire = lazy(() => import("./pages/blogs/MultipleProfilesBackfire"));
const GMBReinstatementRequest = lazy(() => import("./pages/blogs/GMBReinstatementRequest"));
const SampleAppealTemplate = lazy(() => import("./pages/blogs/SampleAppealTemplate"));
const GMBReinstatementDenied = lazy(() => import("./pages/blogs/GMBReinstatementDenied"));
const GMBAccountSuspensionPrevention = lazy(() => import("./pages/blogs/GMBAccountSuspensionPrevention"));
const GMBReinstatementAppealEssentials = lazy(() => import("./pages/blogs/GMBReinstatementAppealEssentials"));
const GMBReinstatementDelays = lazy(() => import("./pages/blogs/GMBReinstatementDelays"));
const ClinicGrowthDigitalStrategies = lazy(() => import("./pages/blogs/ClinicGrowthDigitalStrategies"));
const LocalSEOChecklistClinic = lazy(() => import("./pages/blogs/LocalSEOChecklistClinic"));
const AutoReviewRepliesTool = lazy(() => import("./pages/blogs/AutoReviewRepliesTool"));
const GetPatientsGBPRoadmap = lazy(() => import("./pages/blogs/GetPatientsGBPRoadmap"));
const GMBMaintenancePlan = lazy(() => import("./pages/blogs/GMBMaintenancePlan"));
const GMBProfileAudits = lazy(() => import("./pages/blogs/GMBProfileAudits"));
const AgencySuspensionProofGBP = lazy(() => import("./pages/blogs/AgencySuspensionProofGBP"));
const AgencyManpowerFlyAgent = lazy(() => import("./pages/blogs/AgencyManpowerFlyAgent"));
// From main merge — new tools + blog articles added upstream.
const WebsiteGeoScan = lazy(() => import("./pages/tools/website-geo-scan"));
const AiSearchVisibility = lazy(() => import("./pages/tools/ai-search-visibility"));
const SeoSiteAudit = lazy(() => import("./pages/tools/seo-site-audit"));
const ToolsIndex = lazy(() => import("./pages/ToolsIndex"));
const SmileScore = lazy(() => import("./pages/tools/smile-score"));
const HearingTest = lazy(() => import("./pages/tools/hearing-test"));
const EyeTest = lazy(() => import("./pages/tools/eye-test"));
const GooglePostsHealthCheck = lazy(() => import("./pages/tools/google-posts-health-check"));
// ProductHuntLanding (from main merge) intentionally NOT lazy-imported
// here — /producthunt renders our ProductHuntV2 landing directly (see
// the const above). V1 was archived to docs/archive/producthunt-v1/ on
// 2026-07-03 and removed from active code to keep the shipped bundle
// light. Keeping the reference commented so the intent is discoverable
// at review time:
// const ProductHuntLanding = lazy(() => import("./pages/ProductHuntLanding"));
const GbpAuditImproveLocalRankings = lazy(() => import("./pages/blogs/GbpAuditImproveLocalRankings"));
const LocalRankChecker = lazy(() => import("./pages/blogs/LocalRankChecker"));
const AgenciesGbpAuditLeads = lazy(() => import("./pages/blogs/AgenciesGbpAuditLeads"));
const FlySocialToolsGuide = lazy(() => import("./pages/blogs/FlySocialToolsGuide"));
import Agency from "./pages/Agency";
import Individuals from "./pages/Individuals";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import Pricing from "./pages/Pricing";
import HealthcareLanding from "./pages/HealthcareLanding";
const SinglePost = lazy(() => import("./pages/gmb/SinglePost"));
const EditPost = lazy(() => import("./pages/gmb/EditPost"));
import SubdomainRouter from "./pages/subdomain/SubdomainRouter";
const FlyAgentForClaude = lazy(() => import("./pages/mcp/FlyAgentForClaude"));
const AgentPage = lazy(() => import("./pages/mcp/AgentPage"));
const AgenciesPage = lazy(() => import("./pages/mcp/AgenciesPage"));
const MultiLocationPage = lazy(() => import("./pages/mcp/MultiLocationPage"));
const FreelancersPage = lazy(() => import("./pages/mcp/FreelancersPage"));
const LocalBusinessPage = lazy(() => import("./pages/mcp/LocalBusinessPage"));
// MCP guides barrel — per-name lazy so marketing traffic doesn't ship
// the Claude/ChatGPT/Gemini integration guides.
const ChatGPTGuide             = lazy(() => import("./pages/mcp/guides").then((m) => ({ default: m.ChatGPTGuide })));
const ClaudeGuide              = lazy(() => import("./pages/mcp/guides").then((m) => ({ default: m.ClaudeGuide })));
const GeminiGuide              = lazy(() => import("./pages/mcp/guides").then((m) => ({ default: m.GeminiGuide })));
const CoworkPluginGuide        = lazy(() => import("./pages/mcp/guides").then((m) => ({ default: m.CoworkPluginGuide })));
const AgencyWorkflowsGuide     = lazy(() => import("./pages/mcp/guides").then((m) => ({ default: m.AgencyWorkflowsGuide })));
const GuidesLandingPage        = lazy(() => import("./pages/mcp/guides").then((m) => ({ default: m.GuidesLandingPage })));
import LandingPage from "./pages/subdomain/LandingPage";
import StoreLocatorPage from "./pages/subdomain/StoreLocatorPage";
import RootDomainLandingPage from "./pages/subdomain/RootDomainLandingPage";
import { AuthProvider } from "./pages/fly-app/AuthProvider";
import { WorkspaceProvider } from "./contexts/WorkspaceContext";
import { buildNavigationPath } from '@/utils/agencyRouting';
const AcceptInvitation = lazy(() => import("./pages/fly-app/AcceptInvitation"));
const AcceptLeadsGeneratorInvitation = lazy(() => import("./pages/AcceptLeadsGeneratorInvitation"));
const AppSumoRedeem = lazy(() => import("./pages/AppSumoRedeem"));
const AppSumoWelcome = lazy(() => import("./pages/AppSumoWelcome"));
const AppSumoLicenseWelcome = lazy(() => import("./pages/AppSumoLicenseWelcome"));
// Named-export barrel from ./pages/subscription — each named export is
// individually lazy so the token-gated flows (locations, checkout,
// account details) don't ship with the marketing entry bundle.
const LocationManagement     = lazy(() => import("./pages/subscription").then((m) => ({ default: m.LocationManagement     })));
const SubscriptionManagement = lazy(() => import("./pages/subscription").then((m) => ({ default: m.SubscriptionManagement })));
const Checkout               = lazy(() => import("./pages/subscription").then((m) => ({ default: m.Checkout               })));
const BulkCheckout           = lazy(() => import("./pages/subscription").then((m) => ({ default: m.BulkCheckout           })));
const AccountDetails         = lazy(() => import("./pages/subscription").then((m) => ({ default: m.AccountDetails         })));
// /pages/agent barrel (28 exports) — per-name lazy. None of these
// public-agent flows should ship with the marketing entry bundle.
const PublicTemplatesPage              = lazy(() => import("./pages/agent").then((m) => ({ default: m.PublicTemplatesPage })));
const PublicReferencesPage             = lazy(() => import("./pages/agent").then((m) => ({ default: m.PublicReferencesPage })));
const PublicNewPostPage                = lazy(() => import("./pages/agent").then((m) => ({ default: m.PublicNewPostPage })));
const PublicPhotosPage                 = lazy(() => import("./pages/agent").then((m) => ({ default: m.PublicPhotosPage })));
const PublicSubscriptionsPage          = lazy(() => import("./pages/agent").then((m) => ({ default: m.PublicSubscriptionsPage })));
const PublicCreditsPurchasePage        = lazy(() => import("./pages/agent").then((m) => ({ default: m.PublicCreditsPurchasePage })));
const AgentSubscriptionPage            = lazy(() => import("./pages/agent").then((m) => ({ default: m.AgentSubscriptionPage })));
const PublicIdeasPage                  = lazy(() => import("./pages/agent").then((m) => ({ default: m.PublicIdeasPage })));
const PublicReviewGeneratorPage        = lazy(() => import("./pages/agent").then((m) => ({ default: m.PublicReviewGeneratorPage })));
const PublicReviewResponderPage        = lazy(() => import("./pages/agent").then((m) => ({ default: m.PublicReviewResponderPage })));
const PublicConnectPage                = lazy(() => import("./pages/agent").then((m) => ({ default: m.PublicConnectPage })));
const PublicReconnectPage              = lazy(() => import("./pages/agent").then((m) => ({ default: m.PublicReconnectPage })));
const PublicMicrositeSetupPage         = lazy(() => import("./pages/agent").then((m) => ({ default: m.PublicMicrositeSetupPage })));
const PublicGBPServicesPage            = lazy(() => import("./pages/agent").then((m) => ({ default: m.PublicGBPServicesPage })));
const PublicGBPAttributesPage          = lazy(() => import("./pages/agent").then((m) => ({ default: m.PublicGBPAttributesPage })));
const PublicProfileChangesPage         = lazy(() => import("./pages/agent").then((m) => ({ default: m.PublicProfileChangesPage })));
const PublicDashboardPage              = lazy(() => import("./pages/agent").then((m) => ({ default: m.PublicDashboardPage })));
const PublicOptimizePage               = lazy(() => import("./pages/agent").then((m) => ({ default: m.PublicOptimizePage })));
const PublicKeywordsPage               = lazy(() => import("./pages/agent").then((m) => ({ default: m.PublicKeywordsPage })));
const PublicSocialConnectPage          = lazy(() => import("./pages/agent").then((m) => ({ default: m.PublicSocialConnectPage })));
const PublicSocialPageSelection        = lazy(() => import("./pages/agent").then((m) => ({ default: m.PublicSocialPageSelection })));
const AgentOAuthCallback               = lazy(() => import("./pages/agent").then((m) => ({ default: m.AgentOAuthCallback })));
const AgentLiteLanding                 = lazy(() => import("./pages/agent").then((m) => ({ default: m.AgentLiteLanding })));
const AgentLiteV1Landing               = lazy(() => import("./pages/agent").then((m) => ({ default: m.AgentLiteV1Landing })));
const AutoReviewResponderPage          = lazy(() => import("./pages/agent").then((m) => ({ default: m.AutoReviewResponderPage })));
const NegativeReviewAlertPage          = lazy(() => import("./pages/agent").then((m) => ({ default: m.NegativeReviewAlertPage })));
const ProfileProtectionPage            = lazy(() => import("./pages/agent").then((m) => ({ default: m.ProfileProtectionPage })));
const ReviewGeneratorPage              = lazy(() => import("./pages/agent").then((m) => ({ default: m.ReviewGeneratorPage })));
// /pages/agent/categories barrel — per-name lazy with rename aliases.
const RestaurantsCategoryLanding       = lazy(() => import("./pages/agent/categories").then((m) => ({ default: m.RestaurantsLanding })));
const HealthcareCategoryLanding        = lazy(() => import("./pages/agent/categories").then((m) => ({ default: m.HealthcareLanding })));
const BeautyCategoryLanding            = lazy(() => import("./pages/agent/categories").then((m) => ({ default: m.BeautyLanding })));
const HomeServicesCategoryLanding      = lazy(() => import("./pages/agent/categories").then((m) => ({ default: m.HomeServicesLanding })));
const AutomotiveCategoryLanding        = lazy(() => import("./pages/agent/categories").then((m) => ({ default: m.AutomotiveLanding })));

// Window.__AGENCY_BRANDING__ type is declared in AgencyContext.tsx

// QueryClient is now created in entry-client.tsx and entry-server.tsx
// to support both SSR and CSR

// Helper to get SSR data if available
const getSSRData = () => {
  if (typeof window !== "undefined" && (window as any).__SSR_DATA__) {
    return (window as any).__SSR_DATA__;
  }
  return null;
};

// Helper to check if we're on a subdomain
const isSubdomain = () => {
  // Check for SSR data (available on client after hydration for subdomains)
  const ssrData = getSSRData();
  if (ssrData?.landingData || ssrData?.storeLocatorData) {
    return true;
  }

  // Check if we're on a subdomain via hostname (for client-side routing)
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    const micrositeBaseDomain =
      import.meta.env.VITE_MICROSITE_BASE_DOMAIN || "localhost";
    const appBaseDomain = import.meta.env.VITE_APP_BASE_DOMAIN || "localhost";

    // Check if we're on Vercel (ignore Vercel preview domains)
    if (hostname.includes("vercel.app")) {
      // On Vercel, only treat as subdomain if query param is present
      const params = new URLSearchParams(window.location.search);
      return params.has("subdomain");
    }

    // Main app domains - these are NOT subdomains
    const mainAppDomains = [appBaseDomain, `www.${appBaseDomain}`, "localhost"];
    if (mainAppDomains.includes(hostname)) {
      return false;
    }

    // Check if hostname ends with microsite base domain (e.g., mango.fly-social.site)
    if (
      hostname.endsWith(`.${micrositeBaseDomain}`) &&
      hostname !== micrositeBaseDomain
    ) {
      return true;
    }
  }

  return false;
};

// Helper to check if we're on an agency domain (white-label) or dev mode with ?agency= param
const isAgencyDomain = () => {
  if (typeof window === 'undefined') return false;

  const hostname = window.location.hostname;

  // Known Fly hostnames are ALWAYS the canonical site, never an agency
  // white-label. We check this BEFORE consulting window.__AGENCY_BRANDING__
  // because that flag gets set by AgencyContext when a signed-in user
  // visits /fly-app, and it persists on the window object across SPA
  // navigation. Without this guard, returning to /producthunt or /pricing
  // after the dashboard once would silently re-route the user back to the
  // dashboard on every "/" or "/pricing" hit.
  const flyHosts = [
    'localhost', '127.0.0.1',
    'fly-social.com', 'www.fly-social.com', 'staging.fly-social.com',
    'fly-local-boost.lovable.app',
  ];
  if (flyHosts.some(h => hostname === h || hostname.endsWith(`.${h}`))) {
    // Localhost still honors the explicit ?agency= dev override so we can
    // simulate agency rendering locally.
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('agency')) return true;
      if (sessionStorage.getItem('fly_dev_agency_slug')) return true;
    }
    return false;
  }

  // Production: server-injected agency branding
  if (window.__AGENCY_BRANDING__) return true;

  return false;
};

// Helper to check if we're on the root microsite domain (e.g., fly-social.site without subdomain)
const isRootDomain = () => {
  const ssrData = getSSRData();
  if (ssrData?.subdomain_type === 'root_domain_landing') return true;

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // Never treat localhost as root domain — it's the dev app server
    if (hostname === 'localhost' || hostname === '127.0.0.1') return false;
    const micrositeBaseDomain = import.meta.env.VITE_MICROSITE_BASE_DOMAIN || 'localhost';
    return hostname === micrositeBaseDomain;
  }
  return false;
};

// Legacy /ad → /offer redirect. Preserves ?query params + #hash so any
// live ad campaigns pointing at /ad?utm_source=... land on /offer with
// their UTM tracking intact. Uses `replace` so the browser back button
// skips the /ad step and returns to whatever preceded the click.
const AdToOfferRedirect = () => {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: "/offer", search, hash }} replace />;
};

// Component to check if we're on a subdomain or agency domain and route accordingly
const RootRouter = () => {
  // Agency domains serve app directly at / (clean URLs)
  // LocalSEOApp handles ?invite-link= parameter directly
  if (isAgencyDomain()) {
    return <LocalSEOApp />;
  }

  // Check for invite-link parameter and redirect to accept invitation flow
  // Only needed on main domain to redirect from / to /fly-app
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const inviteLink = params.get('invite-link');
    if (inviteLink) {
      // Redirect to fly-app with invite-link param for AcceptInvitation handling
      window.location.href = buildNavigationPath(`/?invite-link=${inviteLink}`);
      return null;
    }
  }
  if (isSubdomain()) {
    return <SubdomainRouter />;
  }
  // Main domain landing: the Lead Gen Tools landing page is the
  // public-facing front door for now while we run that launch. The
  // original Fly home page stays reachable at /home (see the route
  // definition below) so internal links, deeplinks, and anyone who
  // needs the old hero can still hit it via the explicit suffix.
  return <LeadGenToolsLanding />;
};

// Renders CrispChatWrapper + FlyChatWidget only after the browser is
// idle (or 2.5s from mount, whichever fires first). The widgets pull
// third-party scripts + do their own fetches on mount, which used to
// compete with first-contentful-paint on marketing pages. Deferring
// them off the critical path is a pure perf win — users who don't
// open a chat within 2.5s don't notice a thing.
function DeferredChatWidgets() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let timer: number | undefined;
    let idleId: number | undefined;
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    const cic = (window as unknown as { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
    if (typeof ric === "function") {
      idleId = ric(() => setReady(true), { timeout: 2500 });
    } else {
      timer = window.setTimeout(() => setReady(true), 2500);
    }
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
      if (idleId !== undefined && typeof cic === "function") cic(idleId);
    };
  }, []);
  if (!ready) return null;
  // ErrorBoundary isolates any widget mount crash (e.g. an insecure-
  // context throw from crypto.randomUUID in older Safari before the
  // safe wrapper landed, a third-party SDK exception) so the rest of
  // the page keeps rendering. Prior to this the widget's error would
  // bubble up to the top-level boundary and blank the host page.
  // fallback={null} = if a widget crashes, render nothing here (the
  // widget just quietly disappears). Without an explicit fallback the
  // default full-page error card would blank the host marketing page.
  return (
    <ErrorBoundary fallback={null}>
      <Suspense fallback={null}>
        <CrispChatWrapper />
        <FlyChatWidget />
      </Suspense>
    </ErrorBoundary>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

const App = () => {
  // Embed routes: bypass all SSR / subdomain / agency-domain detection
  // and render the embed tool directly. Embed pages are loaded inside
  // third-party site iframes; the fly-app shell + agency providers
  // would either error out (no agency context for the slug being
  // unknown to subdomain detection) or render the wrong UI. We do this
  // before any other branching so iframe loads stay fast.
  const isEmbedRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/embed/');
  if (isEmbedRoute) {
    return (
      <>
        <Toaster />
        <Sonner />
        <ErrorBoundary>
          <Routes>
            <Route path="/embed/:agencySlug/:toolSlug" element={<EmbedToolPage />} />
          </Routes>
        </ErrorBoundary>
      </>
    );
  }

  // Get SSR data for initial hydration
  const ssrData = getSSRData();

  // Track if we've completed initial hydration
  // After hydration, we clear SSR data so Routes can handle navigation
  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    // After first render (hydration complete), enable normal routing
    if (ssrData && !hasHydrated) {
      setHasHydrated(true);
      // Clear SSR data to prevent it from interfering with future renders
      // But keep it available for components that need initial data
      console.log('[App] Hydration complete, enabling client-side routing');
    }
  }, [ssrData, hasHydrated]);

  // FIRST RENDER ONLY: Use SSR data directly (must match server render for hydration)
  // After hydration, use normal Routes for client-side navigation
  if (ssrData && !hasHydrated) {
    const subdomainType = ssrData.subdomain_type;

    // Extract section from URL for landing pages
    const getActiveSection = () => {
      if (typeof window === 'undefined') return undefined;
      const pathname = window.location.pathname;
      const parts = pathname.split('/').filter(Boolean);
      return parts.length > 0 ? parts[0] : undefined;
    };

    // Get subdomain from hostname
    const getSubdomain = () => {
      if (typeof window === 'undefined') return undefined;
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      if (parts.length >= 2 && parts[0] !== 'localhost' && parts[0] !== 'www') {
        return parts[0];
      }
      return undefined;
    };

    if (subdomainType === 'root_domain_landing') {
      return <RootDomainLandingPage ssrData={ssrData.rootDomainData} />;
    }

    if (subdomainType === 'landing') {
      return (
        <LandingPage
          ssrData={ssrData}
          subdomain={getSubdomain()}
          activeSection={getActiveSection()}
        />
      );
    }

    if (subdomainType === 'store_locator') {
      const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
      const parts = pathname.split('/').filter(Boolean);
      const locationSlug = parts.length > 0 ? parts[0] : undefined;

      return (
        <StoreLocatorPage
          ssrData={ssrData.storeLocatorData}
          subdomain={getSubdomain()}
          locationSlug={locationSlug}
        />
      );
    }
  }

  // Root domain landing page (fly-social.site without subdomain)
  if (isRootDomain()) {
    return <RootDomainLandingPage ssrData={getSSRData()?.rootDomainData} />;
  }

  // After hydration OR if no SSR data: use SubdomainRouter for navigation
  if (isSubdomain()) {
    return (
      <>
        {/* Chatwoot widget is unmounted while we build our own chat
            stack to replace it (see chat_* tables + fly-chat-bot edge
            function in progress). The edge function + knowledge.md
            stay deployed so the bot logic is ready to wire up to the
            new widget. CrispChatWrapper stays as the live fallback
            chat on the subdomain surfaces it covers. Deferred so
            first-contentful-paint isn't blocked by its script fetch. */}
        <DeferredChatWidgets />
        <Toaster />
        <Sonner />
        <ScrollToTop />
        <GA4PageView />
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<SubdomainRouter />} />
            <Route path="/:section" element={<SubdomainRouter />} />
            <Route path="/:section/:slug" element={<SubdomainRouter />} />
            <Route path="*" element={<SubdomainRouter />} />
          </Routes>
        </ErrorBoundary>
      </>
    );
  }

  // Main domain routes
  return (
    <>
      {/* Fly's own visitor chat + Crisp human-handoff fallback. Both
          are mounted via DeferredChatWidgets — they only render after
          the browser is idle (or 2.5s post-mount) so their JS + script
          fetches don't compete with first-contentful-paint. */}
      <DeferredChatWidgets />
      <Toaster />
      <Sonner />
      <ScrollToTop />
      {/* GA4PageView is the only thing that runs UTM capture across
          route changes (via useGA4's unconditional UTM-capture effect).
          It was previously mounted ONLY inside the subdomain branch
          above — meaning every fly-social.com page (/producthunt,
          /pricing, /tools/<slug>, /fly-app/*) had ZERO UTM persistence
          and every event row landed with utm_* = unknown. */}
      <GA4PageView />
      {/* First-party page-view beacon: records where fly-social.com's own
          traffic comes from (Google, social, direct, campaigns) into
          site_traffic_views, for the UTM Traffic cockpit. Self-gates to
          public marketing pages. */}
      <SiteTrafficBeacon />
      <ErrorBoundary>
        {/* Outer Suspense boundary covers every lazy() route element.
            /tools/*, /fly-app/*, /subscription/*, LocalSEOApp are all
            lazy — a first-time /producthunt visitor no longer pays for
            their code. Fallback matches the site background so the
            chunk swap is invisible during navigation. */}
        <Suspense fallback={<div className="min-h-screen bg-[#08080f]" />}>
        <Routes>
          <Route path="/" element={<RootRouter />} />
          {/* Legacy Fly home — still reachable via /home suffix while the
              Lead Gen Tools landing owns the root. Internal links + the
              top nav fall back to this when needed. */}
          <Route path="/home" element={<FlyHomePage />} />
          <Route path="/agency" element={<Agency />} />
          <Route path="/individuals" element={<Individuals />} />
          <Route path="/healthcare" element={<HealthcareLanding />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/pricing" element={<Pricing />} />
          {/* main's ProductHuntLanding route from this section was
              removed — /producthunt is wired further below using our
              ProductHuntV2 landing directly (V1 was archived to
              docs/archive/producthunt-v1/ on 2026-07-03). */}
          {/* Standalone account hub — outside the dashboard so lead-gen-only
              users can reach it (the dashboard's internal guard would
              bounce them otherwise). Suspense-wrapped so the lazy chunk
              shows a quick fallback instead of a flash of nothing. */}
          <Route
            path="/account"
            element={
              <Suspense fallback={<div className="min-h-screen bg-[#08080f]" />}>
                <AccountPage />
              </Suspense>
            }
          />
          {/* Solutions routes removed - site is now product-centric */}
          <Route
            path="/comparisons/fly-vs-brightlocal"
            element={<FlyVsBrightLocal />}
          />
          <Route
            path="/comparisons/fly-vs-moz-local"
            element={<FlyVsMozLocal />}
          />
          <Route path="/comparisons/fly-vs-yext" element={<FlyVsYext />} />

          {/* Feature Pages removed - site is now product-centric */}
          {/* Public tools directory: all 13 free tools, each linking to its try page. */}
          <Route path="/tools" element={<ToolsIndex />} />
          <Route path="/tools/local-rank-check" element={<LocalRankCheck />} />
          <Route path="/tools/gmb-audit" element={<GMBAudit />} />
          <Route path="/tools/gbp-description-generator" element={<GbpDescriptionGenerator />} />
          <Route path="/tools/review-response-generator" element={<ReviewResponseGenerator />} />
          <Route path="/tools/smm-roi-calculator" element={<SmmRoiCalculator />} />
          <Route path="/tools/caption-generator" element={<CaptionGenerator />} />
          <Route path="/tools/brand-voice-analyzer" element={<BrandVoiceAnalyzer />} />
          <Route path="/tools/content-calendar-generator" element={<ContentCalendarGenerator />} />
          <Route path="/tools/linkedin-audit" element={<LinkedInAudit />} />
          <Route path="/tools/reputation-audit" element={<ReputationAudit />} />
          <Route path="/tools/instagram-audit" element={<InstagramAudit />} />
          <Route path="/tools/citation-audit-deep" element={<CitationAuditDeep />} />
          {/* Sprint 8: gbp-posts-score and post-quality-score now alias to
              the unified Google Posts Health Check tool. Old standalone
              implementations stay imported for emergency rollback but the
              live routes point at the merged page. */}
          <Route path="/tools/gbp-posts-score" element={<GooglePostsHealthCheck />} />
          <Route path="/tools/google-posts-health-check" element={<GooglePostsHealthCheck />} />
          <Route path="/tools/restaurant-audit" element={<RestaurantAudit />} />
          <Route path="/tools/website-local-seo-audit" element={<WebsiteLocalSeoAudit />} />
          <Route path="/tools/website-geo-scan" element={<WebsiteGeoScan />} />
          <Route path="/tools/ai-search-visibility" element={<AiSearchVisibility />} />
          <Route path="/tools/seo-site-audit" element={<SeoSiteAudit />} />
          <Route path="/tools/schema-validator" element={<SchemaValidator />} />
          <Route path="/tools/smile-score" element={<SmileScore />} />
          <Route path="/tools/hearing-test" element={<HearingTest />} />
          <Route path="/tools/eye-test" element={<EyeTest />} />
          <Route path="/tools/social-profile-audit" element={<SocialProfileAudit />} />
          <Route path="/tools/engagement-rate-calculator" element={<EngagementRateCalculator />} />
          <Route path="/tools/fb-ads-library-audit" element={<FbAdsLibraryAudit />} />
          <Route path="/tools/competitor-social-audit" element={<CompetitorSocialAudit />} />
          <Route path="/tools/hashtag-research" element={<HashtagResearch />} />
          <Route path="/tools/best-time-to-post" element={<BestTimeToPost />} />
          <Route path="/tools/competitor-analysis-nearby" element={<CompetitorAnalysisNearby />} />
          <Route path="/tools/post-quality-score" element={<GooglePostsHealthCheck />} />
          <Route path="/tools/review-analysis" element={<ReviewAnalysis />} />
          <Route path="/tools/citation-checker" element={<CitationChecker />} />
          <Route path="/tools/review-qr" element={<FreeReviewQRGenerator />} />
          <Route path="/reviewqr" element={<ReviewQRPage />} />
          <Route path="/reviewqr/thank-you" element={<ReviewQRThankYouPage />} />
          <Route
            path="/tools/content-ideas"
            element={<FreeContentIdeaGenerator />}
          />
          <Route
            path="/tools/social-post-generator"
            element={<SocialMediaPostGenerator />}
          />
          <Route
            path="/tools/review-generator"
            element={<ReviewGeneratorTool />}
          />
          <Route
            path="/tools/landing-page-generator"
            element={<LandingPageGenerator />}
          />
          <Route
            path="/tools/profile-protection"
            element={<ProfileProtection />}
          />
          <Route
            path="/tools/free-microsite-builder"
            element={<FreeMicrositeTool />}
          />
          <Route
            path="/tools/website-builder"
            element={<FreeWebsiteBuilder />}
          />
          {/* Redirects for removed tools */}
          <Route
            path="/tools/doctor-citation-checklist"
            element={<Navigate to="/tools/gmb-audit" replace />}
          />
          <Route
            path="/tools/gbp-health-quiz"
            element={<Navigate to="/tools/gmb-audit" replace />}
          />
          <Route path="/tools/content-wizard" element={<FreeContentWizard />} />

          {/* Lead Gen Tools marketing landing — replaces the Free Tools
              dropdown in the marketing nav. Tool URLs (/tools/gmb-audit,
              etc.) stay accessible because they double as embed targets
              for agencies; this page is just the landing umbrella. */}
          {/* /lead-gen-tools serves two audiences depending on context:
              - public marketing visitors on main fly-social.com get the
                LeadGenToolsLanding landing page,
              - lead-gen-only invitees / agency owners on a white-label
                domain need the dashboard, so we hand off to LocalSEOApp
                and let its internal router resolve the page. The
                /fly-app/lead-gen-tools alias below covers admins on
                the main domain via buildNavigationPath. */}
          <Route path="/lead-gen-tools" element={isAgencyDomain() ? <LocalSEOApp /> : <LeadGenToolsLanding />} />

          {/* Embeddable Tools for Agencies */}
          <Route path="/embed/:agencySlug/:toolSlug" element={<EmbedToolPage />} />

          {/* GoHighLevel marketplace Custom Page (rendered inside GHL's iframe).
              Canonical path is /crm — GHL rejects URLs containing "ghl" or
              "highlevel" for white-label-friendly apps (agencies hide every
              HighLevel reference from their clients). /ghl stays as an
              internal alias for the dev simulator + old links. */}
          <Route path="/crm" element={<GhlAppPage />} />
          <Route path="/ghl" element={<GhlAppPage />} />

          {/* Public Landing Pages */}
          <Route path="/p/:slug" element={<PublicLandingPage />} />
          <Route path="/review/:publicLink" element={<PublicReviewPage />} />
          <Route
            path="/content-review/:shareToken"
            element={<ContentReviewPage />}
          />
          <Route path="/verify-whatsapp" element={<VerifyWhatsApp />} />
          <Route
            path="/agent/profile-review/:token"
            element={<ProfileUpdateReview />}
          />
          <Route
            path="/client-content-generator/:shareToken"
            element={<ClientContentGeneratorPage />}
          />
          <Route
            path="/references/:token"
            element={<PublicReferenceImagesPage />}
          />
          <Route path="/onboard/:token" element={<PublicOnboardingPage />} />
          <Route path="/optimize/:token" element={<PublicOnboardingPage />} />
          <Route
            path="/analytics/:generatorId"
            element={<PublicAnalyticsPage />}
          />
          <Route path="/wa-onboard" element={<WhatsAppOnboarding />} />
          <Route
            path="/wa-onboard/callback"
            element={<WhatsAppOnboardingCallback />}
          />
          <Route
            path="/email/preferences/:email"
            element={<EmailUnsubscribe />}
          />

          {/* Subscription Management Pages (Token-based, no auth required) */}
          <Route
            path="/manage/locations/:token"
            element={<LocationManagement />}
          />
          <Route
            path="/manage/subscription/:token"
            element={<SubscriptionManagement />}
          />
          <Route path="/checkout/:token" element={<Checkout />} />
          <Route path="/checkout/bulk/:token" element={<BulkCheckout />} />
          <Route path="/account/:token" element={<AccountDetails />} />

          {/* Standalone account hub — outside LocalSEOApp so lead-gen-only
              users can reach it (LocalSEOApp's internal guard would bounce
              them otherwise). Linked from the avatar dropdown in
              SignInButton. Suspense fallback matches the site bg so the
              lazy chunk swap is invisible. */}
          <Route
            path="/account"
            element={
              <Suspense fallback={<div className="min-h-screen bg-[#08080f]" />}>
                <AccountPage />
              </Suspense>
            }
          />

          {/* Fly Agent Pages */}
          <Route path="/fly-agent" element={<FlyAgentForClaude />} />
          <Route path="/fly-agent/claude" element={<FlyAgentForClaude />} />
          <Route path="/fly-agent/agencies" element={<AgenciesPage />} />
          <Route path="/fly-agent/multi-location" element={<MultiLocationPage />} />
          <Route path="/fly-agent/freelancers" element={<FreelancersPage />} />
          <Route path="/fly-agent/local-business" element={<LocalBusinessPage />} />
          {/* Fly Agent Integration Guides */}
          <Route path="/fly-agent/guides" element={<GuidesLandingPage />} />
          <Route path="/fly-agent/guides/chatgpt" element={<ChatGPTGuide />} />
          <Route path="/fly-agent/guides/claude" element={<ClaudeGuide />} />
          <Route path="/fly-agent/guides/gemini" element={<GeminiGuide />} />
          <Route path="/fly-agent/guides/cowork-plugin" element={<CoworkPluginGuide />} />
          <Route path="/fly-agent/guides/agency-workflows" element={<AgencyWorkflowsGuide />} />
          {/* Dynamic agent pages (catch-all must be after specific routes) */}
          <Route path="/fly-agent/:agentId" element={<AgentPage />} />
          {/* Legacy redirects */}
          <Route path="/fly-agent-for-claude" element={<Navigate to="/fly-agent/claude" replace />} />
          <Route path="/mcp/:agentId" element={<Navigate to="/fly-agent/:agentId" replace />} />

          {/* Main Product Pages */}
          <Route path="/managed" element={<ManagedPage />} />
          <Route path="/managed/thank-you" element={<ManagedThankYouPage />} />
          <Route path="/pay/:id" element={<ManagedInvoice />} />
          <Route
            path="/producthunt"
            element={
              <Suspense fallback={<div className="min-h-screen bg-[#08080f]" />}>
                <ProductHuntPage />
              </Suspense>
            }
          />
          {/* /offer — standalone offer-funnel landing. Uses the V2 design
              as a base for now; SignInButton + AccountPage treat /offer as
              a first-class lead-gen funnel path (same funnel routing as
              /producthunt). Suspense fallback matches the site bg so
              the lazy chunk swap is invisible.

              /ad is the previous URL and stays alive as a permanent
              redirect to /offer so any external ad links, bookmarks, or
              inbound campaigns that still point at /ad keep working. */}
          <Route
            path="/offer"
            element={
              <Suspense fallback={<div className="min-h-screen bg-[#08080f]" />}>
                <OfferPage />
              </Suspense>
            }
          />
          <Route path="/ad" element={<AdToOfferRedirect />} />
          <Route path="/agencies" element={<FlyAgenciesPage />} />
          <Route path="/agencies-fly-web" element={<AgenciesFlyWebPage />} />
          <Route path="/agencies-fly-web/thank-you" element={<AgenciesFlyWebThankYouPage />} />
          <Route path="/agencies-fly-agent" element={<AgenciesFlyAgentPage />} />
          <Route path="/fly-diy" element={<FlyDIYPage />} />

          {/* GBP WhatsApp Assistant */}
          <Route path="/gmb-whatsapp-assistant" element={<GmbWhatsappAssistant />} />
          <Route path="/gmb-whatsapp-assistant/pro" element={<GmbWhatsappAssistant />} />
          <Route path="/gmb-whatsapp-assistant/max" element={<GmbWhatsappAssistant />} />

          {/* Legacy redirects for Agent Lite */}
          <Route path="/agent/lite" element={<Navigate to="/gmb-whatsapp-assistant" replace />} />
          <Route path="/fly/lite" element={<Navigate to="/gmb-whatsapp-assistant" replace />} />

          {/* Agent Landing Pages (kept for sub-pages) */}
          <Route path="/agent/lite-v1" element={<AgentLiteV1Landing />} />
          <Route
            path="/agent/lite/auto-review-responder"
            element={<AutoReviewResponderPage />}
          />
          <Route
            path="/agent/lite/negative-review-alert"
            element={<NegativeReviewAlertPage />}
          />
          <Route
            path="/agent/lite/profile-protection"
            element={<ProfileProtectionPage />}
          />
          <Route
            path="/agent/lite/review-generator"
            element={<ReviewGeneratorPage />}
          />

          {/* Agent Lite Category Landing Pages */}
          <Route
            path="/agent/lite/restaurants"
            element={<RestaurantsCategoryLanding />}
          />
          <Route
            path="/agent/lite/healthcare"
            element={<HealthcareCategoryLanding />}
          />
          <Route
            path="/agent/lite/beauty"
            element={<BeautyCategoryLanding />}
          />
          <Route
            path="/agent/lite/home-services"
            element={<HomeServicesCategoryLanding />}
          />
          <Route
            path="/agent/lite/automotive"
            element={<AutomotiveCategoryLanding />}
          />

          {/* Agent Shareable Links (Token-based, no auth required) */}
          <Route
            path="/agent/templates/:token"
            element={<PublicTemplatesPage />}
          />
          <Route
            path="/agent/references/:token"
            element={<PublicReferencesPage />}
          />
          <Route
            path="/agent/new-post/:token"
            element={<PublicNewPostPage />}
          />
          <Route
            path="/agent/photos/:token"
            element={<PublicPhotosPage />}
          />
          <Route
            path="/agent/subscriptions/:token"
            element={<PublicSubscriptionsPage />}
          />
          <Route
            path="/agent/credits-purchase/:token"
            element={<PublicCreditsPurchasePage />}
          />
          <Route
            path="/agent/subscription/:token"
            element={<AgentSubscriptionPage />}
          />
          <Route path="/agent/ideas/:token" element={<PublicIdeasPage />} />
          <Route
            path="/agent/review-generator-setup/:token"
            element={<PublicReviewGeneratorPage />}
          />
          <Route
            path="/agent/review-responder-setup/:token"
            element={<PublicReviewResponderPage />}
          />
          <Route path="/agent/connect/:token" element={<PublicConnectPage />} />
          <Route
            path="/agent/reconnect/:token"
            element={<PublicReconnectPage />}
          />
          <Route
            path="/agent/microsite-setup/:token"
            element={<PublicMicrositeSetupPage />}
          />
          <Route
            path="/agent/gbp-services/:token"
            element={<PublicGBPServicesPage />}
          />
          <Route
            path="/agent/gbp-attributes/:token"
            element={<PublicGBPAttributesPage />}
          />
          <Route
            path="/agent/profile-changes/:token"
            element={<PublicProfileChangesPage />}
          />
          <Route
            path="/agent/dashboard/:token"
            element={<PublicDashboardPage />}
          />
          <Route
            path="/agent/audit-report/:token"
            element={<PublicOptimizePage />}
          />
          <Route
            path="/agent/keywords/:token"
            element={<PublicKeywordsPage />}
          />
          <Route
            path="/agent/social-connect/:token"
            element={<PublicSocialConnectPage />}
          />
          <Route
            path="/agent/social-select-pages"
            element={<PublicSocialPageSelection />}
          />
          {/* Standalone popup route for OAuth page/account selection (no app shell) */}
          <Route
            path="/oauth/select-pages"
            element={<PublicSocialPageSelection />}
          />
          <Route
            path="/agent/oauth-callback"
            element={<AgentOAuthCallback />}
          />

          {/* Team Invite Acceptance Route */}
          <Route path="/accept-invite" element={
            <AuthProvider>
              <WorkspaceProvider>
                <AcceptInvitation />
              </WorkspaceProvider>
            </AuthProvider>
          } />

          {/* Leads-Generator Invitation Acceptance — separate flow because
              we provision a new agency on accept rather than associating
              the user with an existing one. Doesn't need WorkspaceProvider:
              the page handles its own auth + the redirect afterwards. */}
          <Route path="/accept-leads-generator-invitation" element={
            <AuthProvider>
              <AcceptLeadsGeneratorInvitation />
            </AuthProvider>
          } />

          {/* AppSumo redemption — public landing at fly-social.com/redeem.
              Two-phase flow:
                1. /redeem            — buyer pastes the code, signs in
                                        (Google OAuth stashes the code so it
                                        survives the round-trip), we RESERVE
                                        the code (30-min soft lock).
                2. /redeem/welcome    — buyer enters agency name + phone,
                                        commit fires and the actual grant
                                        (agency + credits + subscription)
                                        happens. Success card renders here.
              See supabase/functions/appsumo-{reserve,commit,release} +
              supabase/migrations/20260707000100_appsumo_reservation.sql. */}
          <Route path="/redeem" element={
            <AuthProvider>
              <AppSumoRedeem />
            </AuthProvider>
          } />
          <Route path="/redeem/welcome" element={
            <AuthProvider>
              <AppSumoWelcome />
            </AuthProvider>
          } />

          {/* AppSumo LICENSE flow (Licensing API v2, deal 225695) — public
              welcome page at /appsumo/welcome. appsumo-oauth-redirect 302s
              buyers here with ?appsumo_license=<key>; the page owns the whole
              claim (peek deal, Google sign-in returning here, agency name +
              phone for new buyers, license link, success + go to dashboard).
              Distinct from the dormant CODE flow above (/redeem). */}
          <Route path="/appsumo/welcome" element={
            <AuthProvider>
              <AppSumoLicenseWelcome />
            </AuthProvider>
          } />

          {/* Login Route - for agency custom domains */}
          <Route path="/login" element={<LocalSEOApp />} />

          {/* Agency White-Label Routes (clean URLs without /fly-app prefix) */}
          <Route path="/app" element={<LocalSEOApp />} />
          <Route path="/oauth-callback" element={<LocalSEOApp />} />
          {/* Transient path set by AgencyOAuthProxyShell.window.history.replaceState
              during agency OAuth proxy hops. Was 404'ing because it wasn't
              declared as a route (Sentry: connecting-404-route). Renders the
              same shell so the ~2s in-between-state doesn't bounce to NotFound. */}
          <Route path="/connecting" element={<LocalSEOApp />} />
          <Route path="/onboarding" element={<LocalSEOApp />} />
          <Route path="/reviews" element={<LocalSEOApp />} />
          <Route path="/content" element={<LocalSEOApp />} />
          <Route path="/content-ideas" element={<LocalSEOApp />} />
          <Route path="/bulk-generate" element={<LocalSEOApp />} />
          <Route path="/create-post" element={<LocalSEOApp />} />
          <Route path="/posts" element={<LocalSEOApp />} />
          <Route path="/media" element={<LocalSEOApp />} />
          <Route path="/rankings" element={<LocalSEOApp />} />
          <Route path="/traffic" element={<LocalSEOApp />} />
          <Route path="/search-insights" element={<LocalSEOApp />} />
          <Route path="/performance" element={<LocalSEOApp />} />
          <Route path="/competitors" element={<LocalSEOApp />} />
          <Route path="/tasks" element={<LocalSEOApp />} />
          <Route path="/reports" element={<LocalSEOApp />} />
          <Route path="/review-generator" element={<LocalSEOApp />} />
          <Route path="/settings" element={<LocalSEOApp />} />
          <Route path="/agency-settings" element={<LocalSEOApp />} />
          <Route path="/lead-gen-subscription" element={<LocalSEOApp />} />
          <Route path="/lead-gen-agency-settings" element={<LocalSEOApp />} />
          <Route path="/import" element={<LocalSEOApp />} />
          <Route path="/seo-audit" element={<LocalSEOApp />} />
          <Route path="/profile-health" element={<LocalSEOApp />} />
          <Route path="/profile-alerts" element={<LocalSEOApp />} />
          <Route path="/microsites" element={<LocalSEOApp />} />
          <Route path="/products-services" element={<LocalSEOApp />} />
          <Route path="/blog-manager" element={<LocalSEOApp />} />
          <Route path="/blog-manager/create" element={<LocalSEOApp />} />
          <Route path="/blog-manager/edit/:blogId" element={<LocalSEOApp />} />
          <Route path="/appointments" element={<LocalSEOApp />} />
          <Route path="/subscription" element={<LocalSEOApp />} />
          <Route path="/workspaces/:workspaceId/locations" element={<LocalSEOApp />} />
          <Route path="/workspaces/:workspaceId/locations/:id" element={<LocalSEOApp />} />
          <Route path="/workspaces/:workspaceId/locations/:id/edit" element={<LocalSEOApp />} />
          <Route path="/workspaces/:workspaceId/locations/:id/history" element={<LocalSEOApp />} />
          <Route path="/locations/*" element={<LocalSEOApp />} />
          <Route path="/create-workspace" element={<LocalSEOApp />} />
          <Route path="/auth-callback" element={<LocalSEOApp />} />
          <Route path="/health" element={<LocalSEOApp />} />
          <Route path="/frame-builder" element={<LocalSEOApp />} />
          <Route path="/frame-builder/:frameId" element={<LocalSEOApp />} />
          <Route path="/frame-configuration" element={<LocalSEOApp />} />
          <Route path="/template-builder" element={<LocalSEOApp />} />
          <Route path="/manage-templates" element={<LocalSEOApp />} />
          <Route path="/rankings/workspace/:workspaceId" element={<LocalSEOApp />} />
          <Route path="/review-analytics/:generatorId" element={<LocalSEOApp />} />
          <Route path="/chat" element={<LocalSEOApp />} />
          <Route path="/autonomous" element={<LocalSEOApp />} />
          <Route path="/location/:id" element={<LocalSEOApp />} />
          <Route path="/location/:id/edit" element={<LocalSEOApp />} />
          <Route path="/pricing" element={<LocalSEOApp />} />
          <Route path="/social-accounts" element={<LocalSEOApp />} />
          <Route path="/social-accounts/select-pages" element={<LocalSEOApp />} />
          <Route path="/social-platform-stats" element={<LocalSEOApp />} />
          <Route path="/content-insights" element={<LocalSEOApp />} />
          <Route path="/social-posts" element={<LocalSEOApp />} />
          <Route path="/social-create-post" element={<LocalSEOApp />} />
          <Route path="/social-callback" element={<LocalSEOApp />} />

          {/* GMB Pro App Routes */}
          <Route path="/fly-app" element={<LocalSEOApp />} />
          <Route path="/fly-app/onboarding" element={<LocalSEOApp />} />
          <Route path="/fly-app/auth-callback" element={<LocalSEOApp />} />
          <Route path="/fly-app/oauth-callback" element={<LocalSEOApp />} />
          <Route
            path="/fly-app/workspaces/:workspaceId/locations"
            element={<LocalSEOApp />}
          />
          <Route path="/fly-app/reviews" element={<LocalSEOApp />} />
          <Route path="/fly-app/content" element={<LocalSEOApp />} />
          <Route path="/fly-app/content-ideas" element={<LocalSEOApp />} />
          <Route path="/fly-app/bulk-generate" element={<LocalSEOApp />} />
          <Route
            path="/fly-app/content-ideas/:ideaId/edit"
            element={<ContentIdeaEditPage />}
          />
          <Route path="/fly-app/create-post" element={<LocalSEOApp />} />
          <Route path="/fly-app/posts" element={<LocalSEOApp />} />
          <Route path="/fly-app/media" element={<LocalSEOApp />} />
          <Route path="/fly-app/frame-builder" element={<LocalSEOApp />} />
          <Route
            path="/fly-app/frame-builder/:frameId"
            element={<LocalSEOApp />}
          />
          <Route
            path="/fly-app/frame-configuration"
            element={<LocalSEOApp />}
          />
          <Route path="/fly-app/template-builder" element={<LocalSEOApp />} />
          <Route path="/fly-app/manage-templates" element={<LocalSEOApp />} />
          <Route path="/fly-app/rankings" element={<LocalSEOApp />} />
          <Route
            path="/fly-app/rankings/workspace/:workspaceId"
            element={<LocalSEOApp />}
          />
          <Route path="/fly-app/traffic" element={<LocalSEOApp />} />
          <Route path="/fly-app/search-insights" element={<LocalSEOApp />} />
          <Route path="/fly-app/performance" element={<LocalSEOApp />} />
          <Route path="/fly-app/competitors" element={<LocalSEOApp />} />
          <Route path="/fly-app/health" element={<LocalSEOApp />} />
          <Route path="/fly-app/seo-audit" element={<LocalSEOApp />} />
          <Route path="/fly-app/tasks" element={<LocalSEOApp />} />
          <Route path="/fly-app/profile-alerts" element={<LocalSEOApp />} />
          <Route path="/fly-app/profile-health" element={<LocalSEOApp />} />
          <Route path="/fly-app/ops-command-center" element={<LocalSEOApp />} />
          <Route path="/fly-app/posts-checker" element={<LocalSEOApp />} />
          <Route path="/fly-app/ops-task/:taskId" element={<LocalSEOApp />} />
          <Route path="/fly-app/job-logs" element={<LocalSEOApp />} />
          <Route path="/fly-app/bulk-rank-scanner" element={<LocalSEOApp />} />
          <Route path="/fly-app/reports" element={<LocalSEOApp />} />
          <Route path="/fly-app/review-generator" element={<LocalSEOApp />} />
          <Route
            path="/fly-app/review-analytics/:generatorId"
            element={<LocalSEOApp />}
          />
          <Route path="/fly-app/appointments" element={<LocalSEOApp />} />
          <Route path="/fly-app/settings" element={<LocalSEOApp />} />
          <Route path="/fly-app/blog-manager" element={<LocalSEOApp />} />
          <Route path="/fly-app/blog-manager/create" element={<LocalSEOApp />} />
          <Route path="/fly-app/blog-manager/edit/:blogId" element={<LocalSEOApp />} />
          <Route path="/fly-app/microsites" element={<LocalSEOApp />} />
          <Route path="/fly-app/products-services" element={<LocalSEOApp />} />
          <Route path="/fly-app/lead-gen-tools" element={<LocalSEOApp />} />
          <Route path="/fly-app/lead-gen-subscription" element={<LocalSEOApp />} />
          <Route path="/fly-app/lead-gen-agency-settings" element={<LocalSEOApp />} />
          <Route path="/fly-app/microsite-studio" element={<LocalSEOApp />} />
          <Route
            path="/fly-app/microsite-studio/:sessionId"
            element={<LocalSEOApp />}
          />
          <Route path="/fly-app/create-workspace" element={<LocalSEOApp />} />
          <Route path="/fly-app/founder" element={<LocalSEOApp />} />
          <Route path="/fly-app/leads-dashboard" element={<LocalSEOApp />} />
          <Route
            path="/fly-app/free-tools-dashboard"
            element={<LocalSEOApp />}
          />
          <Route path="/fly-app/import" element={<LocalSEOApp />} />
          <Route path="/fly-app/chat" element={<LocalSEOApp />} />
          <Route path="/fly-app/autonomous" element={<LocalSEOApp />} />
          <Route
            path="/fly-app/admin/rank-dashboard"
            element={<LocalSEOApp />}
          />
          <Route
            path="/fly-app/admin/webhook-events"
            element={<LocalSEOApp />}
          />
          <Route
            path="/fly-app/admin/holiday-calendar"
            element={<LocalSEOApp />}
          />
          <Route
            path="/fly-app/admin/agent-test-runner"
            element={<LocalSEOApp />}
          />
          <Route path="/fly-app/location/:id" element={<LocalSEOApp />} />
          <Route path="/fly-app/location/:id/edit" element={<LocalSEOApp />} />
          <Route
            path="/fly-app/locations/:id/manage"
            element={<LocalSEOApp />}
          />
          <Route path="/fly-app/locations/:id/edit" element={<LocalSEOApp />} />
          <Route
            path="/fly-app/workspaces/:workspaceId/locations/:id"
            element={<LocalSEOApp />}
          />
          <Route
            path="/fly-app/workspaces/:workspaceId/locations/:id/edit"
            element={<LocalSEOApp />}
          />
          <Route
            path="/fly-app/workspaces/:workspaceId/locations/:id/history"
            element={<LocalSEOApp />}
          />
          <Route
            path="/fly-app/workspaces/:workspaceId/locations/:id/manage"
            element={<LocalSEOApp />}
          />
          <Route
            path="/fly-app/keyword/:keywordId"
            element={<KeywordRankDetail />}
          />
          <Route path="/fly-app/subscription" element={<LocalSEOApp />} />
          <Route path="/fly-app/subscription/phones" element={<AuthorizedNumbers />} />
          <Route path="/fly-app/pricing" element={<LocalSEOApp />} />
          <Route path="/fly-app/checkout" element={<SubscriptionCheckout />} />
          <Route path="/fly-app/agency-checkout" element={<LocalSEOApp />} />
          <Route path="/fly-app/welcome" element={<LocalSEOApp />} />
          <Route path="/fly-app/credits" element={<CreditsPurchase />} />
          <Route path="/fly-app/credits/usage" element={<CreditUsage />} />
          <Route path="/fly-app/test" element={<TestGMBPro />} />
          <Route path="/fly-app/debug" element={<GMBProDebug />} />

          {/* Catch-all for any other /fly-app/* routes */}
          <Route path="/fly-app/*" element={<LocalSEOApp />} />

          {/* GMB Pro Simple (Fallback) Routes */}
          <Route path="/fly-app-simple" element={<GMBProSimple />} />
          <Route path="/fly-app-simple/locations" element={<GMBProSimple />} />
          <Route path="/fly-app-simple/reviews" element={<GMBProSimple />} />
          <Route path="/fly-app-simple/content" element={<GMBProSimple />} />
          <Route path="/fly-app-simple/rankings" element={<GMBProSimple />} />
          <Route path="/fly-app-simple/traffic" element={<GMBProSimple />} />
          <Route
            path="/fly-app-simple/competitors"
            element={<GMBProSimple />}
          />
          <Route path="/fly-app-simple/tasks" element={<GMBProSimple />} />
          <Route path="/fly-app-simple/settings" element={<GMBProSimple />} />

          <Route
            path="/test-email-notifications"
            element={<TestEmailNotifications />}
          />
          <Route path="/industries/restaurants" element={<Restaurants />} />
          <Route path="/industries/healthcare" element={<Healthcare />} />
          <Route path="/industries/retail" element={<Retail />} />
          <Route path="/industries/automotive" element={<Automotive />} />
          <Route
            path="/industries/professional-services"
            element={<ProfessionalServices />}
          />
          <Route path="/terms-of-service" element={<Terms />} />
          <Route path="/privacy-policy" element={<Privacy />} />
          <Route path="/cookie-policy" element={<Cookie />} />

          {/* Blog Routes */}
          <Route path="/blogs" element={<BlogIndex />} />
          <Route
            path="/blogs/google-business-profile-suspension"
            element={<GoogleBusinessProfileSuspension />}
          />
          <Route
            path="/blogs/gmb-suspension-recovery-time"
            element={<GMBSuspensionRecoveryTime />}
          />
          <Route
            path="/blogs/posting-fake-reviews-keyword-stuffing"
            element={<PostingFakeReviewsKeywordStuffing />}
          />
          <Route
            path="/blogs/updating-business-info-gmb-suspension"
            element={<UpdatingBusinessInfoGMBSuspension />}
          />
          <Route
            path="/blogs/multiple-profiles-backfire"
            element={<MultipleProfilesBackfire />}
          />
          <Route
            path="/blogs/gmb-reinstatement-request"
            element={<GMBReinstatementRequest />}
          />
          <Route
            path="/blogs/google-business-profile-audit-improve-local-rankings"
            element={<GbpAuditImproveLocalRankings />}
          />
          <Route
            path="/blogs/local-rank-checker"
            element={<LocalRankChecker />}
          />
          <Route
            path="/blogs/agencies-more-local-seo-clients-gbp-audits"
            element={<AgenciesGbpAuditLeads />}
          />
          <Route
            path="/blogs/sample-appeal-template"
            element={<SampleAppealTemplate />}
          />
          <Route
            path="/blogs/gmb-reinstatement-denied"
            element={<GMBReinstatementDenied />}
          />
          <Route
            path="/blogs/gmb-account-suspension-prevention"
            element={<GMBAccountSuspensionPrevention />}
          />
          <Route
            path="/blogs/gmb-reinstatement-appeal-essentials"
            element={<GMBReinstatementAppealEssentials />}
          />
          <Route
            path="/blogs/gmb-reinstatement-delays-tips-workarounds"
            element={<GMBReinstatementDelays />}
          />
          <Route
            path="/blogs/clinic-growth-digital-strategies"
            element={<ClinicGrowthDigitalStrategies />}
          />
          <Route
            path="/blogs/local-seo-checklist-clinic"
            element={<LocalSEOChecklistClinic />}
          />
          <Route
            path="/blogs/auto-review-replies-tool"
            element={<AutoReviewRepliesTool />}
          />
          <Route
            path="/blogs/get-patients-gbp-roadmap"
            element={<GetPatientsGBPRoadmap />}
          />
          <Route
            path="/blogs/gmb-maintenance-plan"
            element={<GMBMaintenancePlan />}
          />
          <Route
            path="/blogs/gmb-profile-audits"
            element={<GMBProfileAudits />}
          />
          <Route
            path="/blogs/agency-suspension-proof-gbp"
            element={<AgencySuspensionProofGBP />}
          />
          <Route
            path="/blogs/agency-manpower-fly-agent"
            element={<AgencyManpowerFlyAgent />}
          />
          <Route
            path="/blogs/fly-social-tools-complete-guide"
            element={<FlySocialToolsGuide />}
          />

          {/* Single Post Routes */}
          <Route
            path="/fly-app/posts/:workspaceId/:postId"
            element={<SinglePost />}
          />
          <Route
            path="/fly-app/posts/:workspaceId/:postId/edit"
            element={
              <AuthProvider>
                <WorkspaceProvider>
                  <LocalSEOApp />
                </WorkspaceProvider>
              </AuthProvider>
            }
          />
          <Route
            path="/fly-app/posts/:workspaceId/:postId/edit-published"
            element={
              <AuthProvider>
                <WorkspaceProvider>
                  <LocalSEOApp />
                </WorkspaceProvider>
              </AuthProvider>
            }
          />
          <Route
            path="/fly-app/social/:workspaceId/:socialPostId/edit"
            element={
              <AuthProvider>
                <WorkspaceProvider>
                  <LocalSEOApp />
                </WorkspaceProvider>
              </AuthProvider>
            }
          />
          <Route
            path="/fly-app/social/:workspaceId/:socialPostId/edit-published"
            element={
              <AuthProvider>
                <WorkspaceProvider>
                  <LocalSEOApp />
                </WorkspaceProvider>
              </AuthProvider>
            }
          />

          {/* Subdomain Pages - For local testing */}
          <Route path="/subdomain/:subdomain" element={<SubdomainRouter />} />
          <Route
            path="/subdomain/:subdomain/:section"
            element={<SubdomainRouter />}
          />
          <Route
            path="/subdomain/:subdomain/:section/:slug"
            element={<SubdomainRouter />}
          />

          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </ErrorBoundary>
    </>
  );
};

interface SentryFallbackProps {
  error: unknown;
  componentStack: string;
  resetError: () => void;
}

const SentryFallback = ({ resetError }: SentryFallbackProps) => {
  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <Card className="w-full max-w-lg bg-white shadow-lg border border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Something went wrong
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-slate-700">
          <p className="text-sm leading-relaxed">
            We hit an unexpected error while rendering this page. Our team has been notified automatically.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button onClick={resetError} variant="outline" className="border-slate-300 text-slate-700">
              Try again
            </Button>
            <Button onClick={handleReload} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Refresh page
            </Button>
          </div>
          <p className="text-xs text-slate-500 pt-2 border-t border-slate-100">
            If this keeps happening, contact support and we will sort it out.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

const AppWithSentry = () => (
  <Sentry.ErrorBoundary
    fallback={(props) => <SentryFallback {...(props as SentryFallbackProps)} />}
    showDialog={false}
  >
    <App />
  </Sentry.ErrorBoundary>
);

export default AppWithSentry;
